
import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { Button, Container, Card, Form, Row, Col, Modal, Alert } from 'react-bootstrap';

const AdminLayout: React.FC = () => {
  const currentSeason = localStorage.getItem('selectedSeason') || '50';
  const storedUser = localStorage.getItem(`fantasyUser_${currentSeason}`);
  let isUserAdmin = false;
  if (storedUser) {
    try {
      const parsed = JSON.parse(storedUser);
      isUserAdmin = Boolean(parsed.isAdmin);
    } catch (_) {}
  }

  const isAuthenticated = localStorage.getItem('isAdminAuthenticated') === 'true' || isUserAdmin;
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState<number[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    const saved = localStorage.getItem('adminSelectedSeason');
    return saved ? parseInt(saved) : 0;
  });
  const [showAddSeason, setShowAddSeason] = useState(false);
  const [newSeasonNum, setNewSeasonNum] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/seasons`);
        if (!response.ok) throw new Error('Failed to fetch seasons');
        const data = await response.json();
        const availableSeasons = data.seasons || [49];
        setSeasons(availableSeasons);
        
        const saved = localStorage.getItem('adminSelectedSeason');
        let current = saved ? parseInt(saved) : 0;

        if (!current || !availableSeasons.includes(current)) {
            current = Math.max(...availableSeasons);
        }
        setSelectedSeason(current);
        localStorage.setItem('adminSelectedSeason', current.toString());
      } catch (err) {
        console.error('Error fetching seasons:', err);
        setSeasons([49]);
        setSelectedSeason(49);
      }
    };
    if (isAuthenticated) {
      fetchSeasons();
    }

    const handleSeasonChangeInternal = async () => {
      setSelectedSeason(parseInt(localStorage.getItem('adminSelectedSeason') || '0'));
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/seasons`);
        if (response.ok) {
          const data = await response.json();
          setSeasons(data.seasons || []);
        }
      } catch (err) {
        console.error('Error refreshing seasons:', err);
      }
    };
    window.addEventListener('adminSeasonChanged', handleSeasonChangeInternal);
    return () => window.removeEventListener('adminSeasonChanged', handleSeasonChangeInternal);
  }, [isAuthenticated]);

  const handleAdminLogout = () => {
    localStorage.removeItem('isAdminAuthenticated');
    navigate('/admin/login');
  };

  const handleSeasonChange = (season: number) => {
    setSelectedSeason(season);
    localStorage.setItem('adminSelectedSeason', season.toString());
    window.dispatchEvent(new Event('adminSeasonChanged'));
  };

  const handleAddSeason = async () => {
    const sNum = parseInt(newSeasonNum);
    if (isNaN(sNum)) return;
    setError(null);
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/seasons`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ season: sNum }),
        });
        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Failed to add season');
        }
        const data = await response.json();
        setSeasons(data.seasons);
        setShowAddSeason(false);
        setNewSeasonNum('');
        handleSeasonChange(sNum);
    } catch (err: any) {
        setError(err.message);
    }
  };

  const handleDeleteSeason = async () => {
    if (!window.confirm(`Are you absolutely sure you want to delete Season ${selectedSeason}? This will IRREVERSIBLY delete ALL leagues, drafts, and scores for this season.`)) {
        return;
    }

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/seasons/${selectedSeason}`, {
            method: 'DELETE',
        });
        
        let data;
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
            data = await response.json();
        }

        if (!response.ok) {
            throw new Error(data?.message || `Server returned ${response.status}`);
        }

        if (!data || !data.seasons) {
            throw new Error('Invalid response from server');
        }
        
        const remainingSeasons = data.seasons;
        setSeasons(remainingSeasons);
        
        if (remainingSeasons.length > 0) {
            const nextSeason = Math.max(...remainingSeasons);
            handleSeasonChange(nextSeason);
        } else {
            // Should not happen if at least one season exists
            setSelectedSeason(0);
        }
    } catch (err: any) {
        alert('Error deleting season: ' + err.message);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <Container className="mt-4">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
          <h4>Admin Panel</h4>
          <div className="d-flex align-items-center">
            <Form.Group controlId="adminSeasonSelect" className="me-3 d-flex align-items-center" style={{ position: 'relative', zIndex: 1051 }}>
              <Form.Label className="mb-0 me-2">Season:</Form.Label>
              <select 
                value={selectedSeason} 
                onChange={(e) => handleSeasonChange(parseInt(e.target.value))}
                className="form-select form-select-sm"
                style={{ width: 'auto', cursor: 'pointer' }}
              >
                {seasons.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button variant="link" size="sm" onClick={() => setShowAddSeason(true)} title="Add New Season">+</Button>
              {seasons.length > 1 && (
                <Button variant="link" size="sm" className="text-danger p-0 ms-1" onClick={handleDeleteSeason} title="Delete Current Season">
                    &times;
                </Button>
              )}
            </Form.Group>
            <Button variant="outline-danger" size="sm" onClick={handleAdminLogout}>Admin Logout</Button>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="alert alert-info py-2 text-center">
            Working on <strong>Season {selectedSeason}</strong>
          </div>
          <Outlet />
        </Card.Body>
      </Card>

      <Modal show={showAddSeason} onHide={() => setShowAddSeason(false)} centered>
        <Modal.Header closeButton>
            <Modal.Title>Add New Season</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group controlId="newSeasonNum">
                <Form.Label>Season Number</Form.Label>
                <Form.Control 
                    type="number" 
                    value={newSeasonNum} 
                    onChange={e => setNewSeasonNum(e.target.value)} 
                    placeholder="e.g. 51"
                />
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddSeason(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAddSeason}>Add Season</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminLayout;
